import { UseCreateBookingInput } from '@calcom/atoms/dist/packages/platform/atoms/hooks/bookings/useCreateBooking';
import { context } from '@shared-services/context-service';
import { localizationService } from '@shared-services/localization-service';
import { getWindowInBrowser } from 'client/RuntimeWindow';
import useEditor from 'client/widget-components/customHooks/useEditor';
import { BuyBookingPayload } from 'client/widget-components/ecom/types/EcomSDK';
import { logBookingError } from './loggingUtils';
import strings from './strings';
import {
    BookerContextType,
    Booking,
    BookingStatus,
    CalEventData,
    CreateBookingInput,
    DesignStyle,
    EventServerModel,
    FeatureMetadata,
    PricingType,
    SeatReferenceBooking,
} from './types';

const str = localizationService.str;

// all locales grabbed from https://github.com/calcom/cal.com/tree/main/apps/web/public/static/locales
const CAL_PROVIDER_LANGUAGES_MAP: Record<string, string> = {
    ar: 'ar',
    az: 'az',
    bg: 'bg',
    cs: 'cs',
    da: 'da',
    de: 'de',
    el: 'el',
    en: 'en',
    es: 'es',
    eu: 'eu',
    fr: 'fr',
    he: 'he',
    hr: 'hr',
    hu: 'hu',
    id: 'id',
    it: 'it',
    iw: 'iw',
    ja: 'ja',
    ko: 'ko',
    nl: 'nl',
    no: 'no',
    pl: 'pl',
    pt: 'pt',
    'pt-br': 'pt-BR',
    ro: 'ro',
    ru: 'ru',
    sk: 'sk',
    sr: 'sr',
    sv: 'sv',
    ta: 'ta',
    tr: 'tr',
    uk: 'uk',
    vi: 'vi',
    zh: 'zh-CN',
    'zh-cn': 'zh-CN',
    'zh-tw': 'zh-TW',
    fi: 'fi',
    lv: 'lv',
    is: 'is',
    ca: 'ca',
    nb: 'nb',
    // absent from calcom locales
    // lt: 'lt',
};

const DEFAULT_LANGUAGE = 'en';

/**
 * The site page's own locale code, exactly as Duda spells it (`fr`, `pt-br`, `de-ch`, `th`) — what the
 * native booker runs on, since our own strings and the booking engine's emails cover every Duda locale.
 * Not to be confused with {@link resolveCalProviderLanguage}, which narrows the same value down to the
 * set cal.com's atoms ship translations for and so turns a Thai site into an English one.
 */
export const resolveSiteLocale = () => {
    if (context.isServer) {
        return DEFAULT_LANGUAGE;
    }
    const { dmAPI } = getWindowInBrowser();
    return dmAPI?.getSiteCurrentLocale?.() || DEFAULT_LANGUAGE;
};

export const resolveCalProviderLanguage = () => {
    // if we are in server return default language.
    // locale code is not available on server for now, but could be postponed since calcom atoms renders on client only
    if (context.isServer) {
        return DEFAULT_LANGUAGE;
    }

    const { dmAPI } = getWindowInBrowser();

    const currentLocale = dmAPI?.getSiteCurrentLocale?.() || '';

    // get short language code
    const [shortLanguage] = currentLocale.split('-');

    return (
        CAL_PROVIDER_LANGUAGES_MAP[currentLocale.toLowerCase()] ||
        CAL_PROVIDER_LANGUAGES_MAP[shortLanguage] ||
        DEFAULT_LANGUAGE
    );
};

export const customClassNames = {
    atomsWrapper: 'atoms-wrapper',
    bookerContainer: 'booker-container',
    eventMetaCustomClassNames: {
        eventMetaContainer: 'event-meta-container',
        eventMetaTitle: 'event-meta-title',
        eventMetaTimezoneSelect: 'event-meta-timezone-select',
        eventMetaChildren: 'event-meta-children', // TODO: ask cal to fix it (not applied) + add more class names
    },
    datePickerCustomClassNames: {
        datePickerContainer: 'date-picker-container',
        datePickerTitle: 'date-picker-title',
        datePickerDays: 'date-picker-days',
        datePickerDate: 'date-picker-date',
        datePickerDatesActive: 'date-picker-dates-active',
        datePickerToggle: 'date-picker-toggle',
    },
    availableTimeSlotsCustomClassNames: {
        availableTimeSlotsContainer: 'available-time-slots-container',
        availableTimeSlotsHeaderContainer:
            'available-time-slots-header-container',
        availableTimeSlotsTitle: 'available-time-slots-title',
        availableTimeSlotsTimeFormatToggle:
            'available-time-slots-time-format-toggle',
        availableTimes: 'available-times',
    },
    confirmStep: {
        confirmButton: 'confirm-button',
        backButton: 'back-button ',
    },
};

export const rescheduleClassNames = {
    atomsWrapper: `${customClassNames.atomsWrapper} reschedule-atoms-wrapper`,
    datePickerCustomClassNames: {
        ...customClassNames.datePickerCustomClassNames,
        datePickerDatesActive: undefined,
    },
};

export const getBookerClassNames = (designStyle: DesignStyle) => ({
    ...customClassNames,
    confirmStep: {
        ...customClassNames.confirmStep,
        backButton: `${customClassNames.confirmStep.backButton} ${
            designStyle === DesignStyle.SITE_THEME ? 'button_2' : ''
        }`,
    },
});

export const getRescheduleClassNames = () => ({
    ...customClassNames,
    ...rescheduleClassNames,
});

export const isBookingValid = (status: BookingStatus) => {
    if (!status) return false;
    return ![BookingStatus.CANCELLED, BookingStatus.REJECTED].includes(status);
};

export const isBookingAccepted = (status?: BookingStatus) => {
    if (!status) return false;
    return status === BookingStatus.ACCEPTED;
};

export const isBookingPending = (status?: BookingStatus) => {
    if (!status) return false;
    return status === BookingStatus.PENDING;
};

const validators = {
    isNonEmptyString: (value: any): value is string => {
        return !!value && typeof value === 'string';
    },
    isInteger: (value: any): value is number => {
        return Number.isInteger(value);
    },
    isNumber: (value: any): value is number => {
        return typeof value === 'number';
    },
    oneOf:
        <T>(options: T[]) =>
        (value: any): value is T => {
            return options.includes(value);
        },
};

type BookerParamKey = keyof Pick<
    BookerContextType,
    | 'clientId'
    | 'apiUrl'
    | 'eventSlug'
    | 'teamId'
    | 'language'
    | 'organizationId'
>;

const bookerParamRules: Record<BookerParamKey, (value: unknown) => boolean> = {
    clientId: validators.isNonEmptyString,
    apiUrl: validators.isNonEmptyString,
    eventSlug: validators.isNonEmptyString,
    teamId: validators.isInteger,
    language: validators.isNonEmptyString,
    organizationId: validators.isInteger,
};

export const validateBookerReceivedParams = (
    bookerContext: BookerContextType,
    isActive: boolean
) => {
    // Only validate when booking is active
    if (!isActive) {
        return;
    }

    const invalidParams: string[] = [];

    (
        Object.entries(bookerParamRules) as [
            BookerParamKey,
            (value: unknown) => boolean
        ][]
    ).forEach(([param, validator]) => {
        if (!validator(bookerContext[param])) {
            invalidParams.push(param);
        }
    });

    if (invalidParams.length > 0) {
        logBookingError('Failed to validate booker received params', {
            additionalInfoToLog: bookerContext,
            invalidParams,
        });
    }
};

const buildFullName = (
    nameResponse: CreateBookingInput['responses']['name']
): string => {
    if (typeof nameResponse === 'string') {
        return nameResponse;
    }

    if ('firstName' in nameResponse && 'lastName' in nameResponse) {
        return [nameResponse.firstName, nameResponse.lastName].join(' ').trim();
    }

    return '';
};

export const bookingObjToEcomConverter = (
    bookingData: UseCreateBookingInput,
    calEventData: CalEventData
): BuyBookingPayload => {
    const hasMultipleDurations = Array.isArray(
        calEventData?.metadata?.multipleDuration
    );
    const castedData = bookingData as CreateBookingInput;
    // thanks calcom for that, responses field present in runtime, but not in types

    return {
        event_name: calEventData?.title || '',
        event_type_id: bookingData.eventTypeId,
        slot_start: bookingData.start,
        time_zone: bookingData.timeZone,
        metadata: bookingData.metadata,
        fields_responses: castedData.responses,
        customer_name: buildFullName(castedData.responses.name),
        customer_email: castedData.responses.email as string,
        ...(bookingData.rrHostSubsetIds?.length && {
            hosts_ids: bookingData.rrHostSubsetIds,
        }),
        ...(hasMultipleDurations && {
            slot_duration: castedData.slot_duration,
        }),
    };
};

export const isPaidBooking = (event: EventServerModel) => {
    return event?.priceInfo?.pricingType === PricingType.PAID;
};

export const shouldUsePaidBooking = ({ currentEvent }: BookerContextType) => {
    return currentEvent ? isPaidBooking(currentEvent) : false;
};

/** Null/undefined behaves like true (show free price). Only explicit false hides it. */
export const shouldShowFreePriceLabel = (
    showFreePrice: boolean | null | undefined
) => showFreePrice !== false;

/** Must mirror BookingPricingMetaSection's early return. */
export const shouldShowPriceMetaSection = (bookerContext: BookerContextType) =>
    shouldUsePaidBooking(bookerContext) ||
    shouldShowFreePriceLabel(bookerContext.showFreePrice);

export const getBookerMetadata = (
    {
        eventIdentifier,
        eventSlug,
        currentEvent,
        isBookingDryRun,
        bookingSource,
    }: BookerContextType,
    calEventData: CalEventData | null
): Record<string, string> => {
    const {
        pricingType,
        formattedBasePrice,
        formattedTaxIncludedPrice,
        taxIncludedPrice,
        basePrice,
        taxIncluded,
    } = currentEvent?.priceInfo || {};

    return {
        calEventId: calEventData?.id ? calEventData.id.toString() : '',
        calEventSlug: eventSlug || '',
        eventId: eventIdentifier,
        eventTitle: calEventData?.title || '',
        pricingType: pricingType || PricingType.FREE,
        formattedBasePrice: formattedBasePrice || '',
        formattedTaxIncludedPrice: formattedTaxIncludedPrice || '',
        taxIncluded: (taxIncluded || false).toString(),
        originalBookingPath: context.isBrowser ? window.location.pathname : '',
        isTest: isBookingDryRun.toString(),
        taxIncludedPrice: taxIncludedPrice?.toString?.() || '',
        basePrice: basePrice?.toString?.() || '',
        bookingSource,
    };
};

export const createDryRunBookingPayload = (): Booking => ({
    title: '',
    userPrimaryEmail: '',
    startTime: new Date().toISOString(),
    user: {
        name: '',
        email: '',
        id: 0,
        timeZone: '',
        username: '',
        avatarUrl: null,
    },
    uid: '',
    endTime: new Date().toISOString(),
    attendees: [],
    status: BookingStatus.ACCEPTED,
    hosts: [],
});

export const isBookingDryRun = (
    editor: ReturnType<typeof useEditor>,
    featureMetadata: FeatureMetadata
) => {
    return (
        editor.isInPreviewOrEditor() ||
        context.isServer ||
        !featureMetadata.isNativeBookingPublishingAvailable
    );
};

export const getSeatReferenceUid = (bookingData: Booking): string => {
    // The first attendee is the customer entry, and seat bookings expose seatUid on attendee objects.
    const attendee = bookingData.attendees[0];
    return 'seatUid' in attendee ? attendee.seatUid : '';
};

export const isSeatReferenceBooking = (
    bookingData: Booking
): bookingData is SeatReferenceBooking => {
    const seatReferenceUid = getSeatReferenceUid(bookingData);
    return !!seatReferenceUid;
};

export const getFormattedPriceStr = (bookerContext: BookerContextType) => {
    const isPaidBooking = shouldUsePaidBooking(bookerContext);

    let priceString = localizationService.str(strings.freeBookingPrice);

    const currentEventPriceInfo = bookerContext.currentEvent?.priceInfo;

    if (isPaidBooking && currentEventPriceInfo?.basePrice) {
        priceString = currentEventPriceInfo.taxIncluded
            ? currentEventPriceInfo?.formattedTaxIncludedPrice || ''
            : currentEventPriceInfo?.formattedBasePrice || '';
    }

    return priceString || '';
};

const extractTimeZoneName = (
    formatter: Intl.DateTimeFormat,
    date: Date
): string =>
    formatter
        .formatToParts(date)
        .filter((part) => part.type === 'timeZoneName')
        .map((part) => part.value)
        .join('')
        .trim();

export const getLocaleTimeZoneName = (
    date: Date,
    locale?: string | string[],
    options?: Intl.DateTimeFormatOptions
) => {
    const baseOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...options,
    };

    return (
        extractTimeZoneName(
            new Intl.DateTimeFormat(locale, {
                ...baseOptions,
                timeZoneName: 'long',
            }),
            date
        ) || Intl.DateTimeFormat().resolvedOptions().timeZone
    );
};

// Cal.com is adding `+{string}` to mails, that have coincidence
// example: example@mail.com ---> example+jnsdc132DSf@mail.com
// cal bug: https://duda.slack.com/lists/T02RF76D2/F09SV1SHML4?record_id=Rec09V484MH6D
export const sanitizeMail = (mailToSanitize: string): string =>
    mailToSanitize.replace(/(\+.*)@/, '@');

// need localized title. temp solution
// cal bug: https://duda.slack.com/lists/T02RF76D2/F09SV1SHML4?record_id=Rec09UJUE9KTM
export const buildBookingTitle = (bookingData: Booking): string => {
    const eventTitle = bookingData.metadata?.eventTitle;
    const staffMemberNames = bookingData.hosts
        .map((host) => host.name)
        .join(', ');
    // Cal and the engine add each guest as an attendee with an empty name (SNIPDUDE-8441).
    const attendeeNames = bookingData.attendees
        ?.filter((attendee) => attendee.name)
        .map((attendee) => attendee.name)
        .join(', ');

    // if any of the values is missing, return the original title
    if (!eventTitle || !staffMemberNames || !attendeeNames) {
        return bookingData.title;
    }

    return str(strings.bookingTitle, {
        eventTitle,
        staffMemberName: staffMemberNames,
        attendeeNames,
    });
};
